import React from 'react';
import { Edit, Delete, DragIndicator } from '@mui/icons-material';
import { IconButton, Chip } from '@mui/material';
import { IOnboardingDocument, IOnboardingTemplateType } from 'core/interfaces/onboardingInterface';
import styles from '../SchedulerSetting.module.scss';

interface DocumentsListProps {
  documents: IOnboardingDocument[];
  templateTypes: IOnboardingTemplateType[];
  onEditClick: (document: IOnboardingDocument) => void;
  onDeleteClick: (document: IOnboardingDocument) => void;
  onReorder?: (documents: IOnboardingDocument[]) => void;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  templateTypes,
  onEditClick,
  onDeleteClick,
}) => {
  // Extract type ID from IRI and find corresponding label
  const getTypeLabel = (typeIRI: string): string => {
    const match = typeIRI.match(/\/(\d+)$/);
    if (!match) return 'Unknown';
    const typeId = Number(match[1]);
    const type = templateTypes.find(t => t.id === typeId);
    return type?.label || 'Unknown';
  };

  const getTypeColor = (typeIRI: string): 'primary' | 'secondary' | 'default' | 'success' | 'error' | 'info' | 'warning' => {
    const match = typeIRI.match(/\/(\d+)$/);
    if (!match) return 'default';
    const typeId = Number(match[1]);
    // Color mapping based on type ID
    switch (typeId) {
      case 1:
        return 'primary';
      case 2:
        return 'secondary';
      case 3:
        return 'info';
      default:
        return 'default';
    }
  };

  if (documents.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No onboarding documents configured yet.</p>
        <p>Click &quot;Add Document&quot; to upload a PDF.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.workplaceTable}>
        <thead>
          <tr>
            <th style={{ width: 48 }}>Order</th>
            <th>Name</th>
            <th>Type</th>
            <th style={{ width: 96, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id}>
              <td style={{ textAlign: 'center', width: 48 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DragIndicator sx={{ color: 'text.secondary', mr: 0.5 }} />
                  <span>{doc.sortOrder}</span>
                </div>
              </td>
              <td>
                <strong>{doc.name}</strong>
              </td>
              <td>
                <Chip
                  label={getTypeLabel(doc.type)}
                  color={getTypeColor(doc.type)}
                  size="small"
                />
              </td>
              <td style={{ textAlign: 'center', width: 96 }}>
                <IconButton
                  size="small"
                  title="Edit"
                  onClick={() => onEditClick(doc)}
                  className={styles.iconButton}
                >
                  <Edit className={styles.editIcon} />
                </IconButton>
                <IconButton
                  size="small"
                  title="Delete"
                  onClick={() => onDeleteClick(doc)}
                  className={styles.iconButton}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DocumentsList;
